import type { CatalogVehicle } from '@makemydeal/dr-activities-change-vehicle';
import { capitalize } from '@makemydeal/dr-common-utils';
import type {
    DataToUpdateTemplate,
    DealTemplate,
    DealTemplateInventoryType,
    ParamsToSaveTemplate
} from '@makemydeal/dr-dash-types';
import type { VppLambdaVehicleCondition } from '@makemydeal/dr-dash-bff-types';
import type { ComboBoxOptions } from '@interstate/components/ComboBox';
import { InventoryType } from '../../constants';
import type { TemplateProcessingInput } from '../../../types';

export const ALL_OPTION = { value: InventoryType.All, label: InventoryType.All };

export const getYearTypeOptions = (yearOptions: string[]) => {
    return [ALL_OPTION, ...yearOptions.map((year: string) => ({ value: year, label: year }))];
};

export const getMakeTypeOptions = (makeOptions: string[]) => {
    return [ALL_OPTION, ...makeOptions.map((make: string) => ({ value: make, label: make }))];
};

export const getModelTypeOptions = (modelOptions: string[]) => {
    return [ALL_OPTION, ...modelOptions.map((model: string) => ({ value: model, label: model }))];
};

export const getTrimTypeOptions = (trimOptions: string[]) => {
    return [ALL_OPTION, ...trimOptions.map((trim: string) => ({ value: trim, label: trim }))];
};

export const getConfigurationTypeOptions = (configurationOptions: CatalogVehicle[]) => {
    // Group configurations by marketingName to identify duplicates
    const groupedByMarketingName = configurationOptions.reduce((acc, config) => {
        const marketingName = config.marketingName ?? '';
        if (!acc[marketingName]) {
            acc[marketingName] = [];
        }
        acc[marketingName].push(config);
        return acc;
    }, {} as Record<string, CatalogVehicle[]>);

    // Helper function to generate a unique label for a configuration
    const generateUniqueLabel = (config: CatalogVehicle, allConfigs: CatalogVehicle[], marketingName: string): string => {
        if (allConfigs.length <= 1) return marketingName;

        const fields: (keyof CatalogVehicle)[] = ['driveLine', 'engineSize', 'fuel', 'transmission'];
        const differentiatingParts: string[] = [];

        // Start with all other configs as potential matches
        let remainingConfigs = allConfigs.filter((c) => c !== config);

        for (const field of fields) {
            const thisValue = String(config[field] || '');

            if (remainingConfigs.length === 0) break;

            // Check if this field can help differentiate from remaining configs
            const configsWithSameValue = remainingConfigs.filter((c) => String(c[field] || '') === thisValue);
            const configsWithDifferentValue = remainingConfigs.filter((c) => String(c[field] || '') !== thisValue);

            // If there are configs with different values for this field, include this field
            if (configsWithDifferentValue.length > 0 && thisValue) {
                differentiatingParts.push(thisValue);
                // Update remaining configs to only those that still match this value
                remainingConfigs = configsWithSameValue;
            }
        }

        if (differentiatingParts.length > 0) {
            return `${marketingName} (${differentiatingParts.join(', ')})`;
        }

        return marketingName;
    };

    // Create options with appropriate labels
    const options = configurationOptions.map((configuration: CatalogVehicle) => {
        const marketingName = configuration.marketingName ?? '';
        const duplicates = groupedByMarketingName[marketingName];

        const label = generateUniqueLabel(configuration, duplicates, marketingName);

        return {
            value: configuration.marketingName !== undefined ? String(configuration.chromeStyleId) : '',
            label: label
        };
    });

    return [ALL_OPTION, ...options];
};

export const getYearForApi = (year: string): string => {
    return year === InventoryType.All ? new Date().getFullYear().toString() : year;
};

export const isValidValue = (value: string): boolean => {
    return Boolean(value && value !== InventoryType.All);
};

export const hasValue = (array: string[]): boolean => {
    return array.length > 0 && Boolean(array[0]);
};

export const hasValidValue = (array: string[]): boolean => {
    return hasValue(array) && isValidValue(array[0]);
};

// Normalize filter values and dropdown options to lowercase for case-insensitive comparison
export const normalizeValue = (value: string): string => {
    return value.toLowerCase();
};

export const arraysMatch = (array1: string[], array2: string[]) => {
    if (array1.length > array2.length) {
        return false;
    }
    return array2.every((item) => array1.includes(item));
};

export enum BuildVehicleSelects {
    inventoryTypes = 'inventoryTypes',
    years = 'years',
    makes = 'makes',
    models = 'models',
    trims = 'trims'
}

const selectOrder = [
    BuildVehicleSelects.inventoryTypes,
    BuildVehicleSelects.years,
    BuildVehicleSelects.makes,
    BuildVehicleSelects.models,
    BuildVehicleSelects.trims
] as const;

export type SelectName = (typeof selectOrder)[number];

export const shouldEnableDropdown = (selectValues: {
    inventoryTypes: DealTemplateInventoryType[];
    years: string[];
    makes: string[];
    models: string[];
    trims: string[];
}) => {
    return (dropdownName: SelectName): boolean => {
        const idx = selectOrder.indexOf(dropdownName);
        for (let i = 0; i < idx; i++) {
            const value = selectValues[selectOrder[i]];
            if (!value || (Array.isArray(value) && value.length === 0)) {
                return false;
            }
        }

        if ([BuildVehicleSelects.models, BuildVehicleSelects.trims].includes(dropdownName)) {
            const yearValue = selectValues[BuildVehicleSelects.years];
            if (Array.isArray(yearValue) && yearValue.includes(InventoryType.All)) {
                return false;
            }
        }

        if (dropdownName === BuildVehicleSelects.models || dropdownName === BuildVehicleSelects.trims) {
            if (Array.isArray(selectValues.makes) && selectValues.makes.length > 1) {
                return false;
            }
        }
        if (dropdownName === BuildVehicleSelects.trims) {
            if (Array.isArray(selectValues.models) && selectValues.models.length > 1) {
                return false;
            }
        }
        const index = selectOrder.indexOf(dropdownName);
        if (index === 0) return true;
        for (let i = 0; i < index; i++) {
            const prevValue = selectValues[selectOrder[i]];
            const prevName = selectOrder[i];
            if (
                [BuildVehicleSelects.makes, BuildVehicleSelects.models, BuildVehicleSelects.trims].includes(prevName) &&
                Array.isArray(prevValue) &&
                prevValue.includes(InventoryType.All)
            ) {
                return false;
            }
        }
        return true;
    };
};

export const normalizeInventoryType = (inventoryType: string) => {
    if (inventoryType.toLowerCase() === 'certified') return InventoryType.CPO;
    return capitalize(inventoryType);
};

export const getInitialInventoryType = (vehicleCondition?: VppLambdaVehicleCondition): InventoryType => {
    if (!vehicleCondition) {
        return InventoryType.All;
    }

    const conditionToInventoryMap: Record<VppLambdaVehicleCondition, InventoryType> = {
        used: InventoryType.Used,
        new: InventoryType.New,
        certified: InventoryType.CPO
    };

    const normalizedCondition = normalizeValue(vehicleCondition) as VppLambdaVehicleCondition;

    return conditionToInventoryMap[normalizedCondition] ?? InventoryType.All;
};

export const areArraysEqual = (arr1: (string | number)[], arr2: (string | number)[]): boolean => {
    if (arr1.length !== arr2.length) return false;
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return sorted1.every((item, index) => item == sorted2[index]);
};

export const hasTemplateChanged = (
    currentValues: {
        templateName: string;
        templateDescription: string;
        inventoryTypes: DealTemplateInventoryType[];
        years: string[];
        makes: string[];
        models: string[];
        trims: string[];
    },
    originalTemplate: DealTemplate | undefined
): boolean => {
    if (!originalTemplate) return true;

    const nameChanged = currentValues.templateName !== originalTemplate.name;
    const descriptionChanged = currentValues.templateDescription !== (originalTemplate.description || '');

    if (nameChanged || descriptionChanged) return true;

    const inventoryTypesChanged = !areArraysEqual(
        currentValues.inventoryTypes.map(String),
        (originalTemplate.inventoryTypes || [InventoryType.All]).map(String)
    );
    const yearsChanged = !areArraysEqual(currentValues.years, originalTemplate.years || [InventoryType.All]);
    const makesChanged = !areArraysEqual(currentValues.makes, originalTemplate.makes || [InventoryType.All]);
    const modelsChanged = !areArraysEqual(currentValues.models, originalTemplate.models || [InventoryType.All]);
    const trimsChanged = !areArraysEqual(currentValues.trims, originalTemplate.trims || [InventoryType.All]);

    return inventoryTypesChanged || yearsChanged || makesChanged || modelsChanged || trimsChanged;
};

export const filterAllValues = <T>(field: T): T | undefined | null => {
    if (field === undefined || field === null) {
        return undefined;
    }

    if (typeof field === 'string') {
        return field.toLowerCase() === 'all' ? null : field;
    }

    if (Array.isArray(field)) {
        if (field.length === 0) {
            return null;
        }

        if (field.length === 1 && typeof field[0] === 'string' && field[0].toLowerCase() === 'all') {
            return null;
        }

        return field;
    }

    return field;
};

export const normalizeYears = (years: string | number | (string | number)[] | undefined | null): number[] | null => {
    if (years === undefined || years === null) return null;
    if (typeof years === 'string') {
        const num = Number(years);
        return isNaN(num) ? null : [num];
    }
    if (typeof years === 'number') {
        return [years];
    }
    if (Array.isArray(years)) {
        const nums = years.map((y) => (typeof y === 'string' ? Number(y) : y)).filter((y) => !isNaN(y));
        return nums.length ? nums : null;
    }
    return null;
};

export const processTemplatePayload = (payload: TemplateProcessingInput): ParamsToSaveTemplate | DataToUpdateTemplate => {
    return {
        ...payload,
        configurations: null,
        inventoryTypes: filterAllValues(payload.inventoryTypes) as DealTemplateInventoryType[],
        makes: filterAllValues(payload.makes),
        models: filterAllValues(payload.models),
        trims: filterAllValues(payload.trims),
        years: normalizeYears(filterAllValues(payload.years))
    };
};

export const autoCorrectInvalidSelections = (
    currentValues: string[],
    availableOptions: ComboBoxOptions,
    setValue: (values: string[]) => void
) => {
    const validValues = currentValues.filter((val) => availableOptions.some((opt: any) => opt.value === val));
    if (currentValues.length > 0 && validValues.length === 0) {
        setValue([InventoryType.All]);
    }
};

export const inventoryOptions = Object.entries(InventoryType).map(([label, value]) => ({ label, value }));
